import { useCallback, useEffect, useRef, useState } from 'react'
import { getOrderCapacityYears } from '@/api/settings.api'
import type { Order } from '@/common/order.common'
import type { CapacityYear } from '@/common/settings.common'
import { getActivitiesInRange } from '@/api/activity.api'
import { getHoursPerUnit } from '@/helper/order.helper'
import { getSibWorkload, loadCountedOrders, type WorkloadBucketDto } from './sibCapacity.api'
import type { ProgressActivity } from './workProgress'

export const SERVICE_CODES = ['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'] as const
export type SibServiceCode = (typeof SERVICE_CODES)[number]

/** A value in the service's own unit (TOJ units, consulting hours, training days, pilot units). */
export type UnitValues = Partial<Record<SibServiceCode, number>>

export type ServiceCapacity = {
  code: SibServiceCode
  /** The agreed quota for the year; null when none is set (0 is a set value). */
  quota: number | null
  /** Quota minus approved orders, from the backend; null when there is no quota to subtract from. */
  free: number | null
}

export type RecipientRemaining = {
  customerId: number
  ordered: UnitValues
  performed: UnitValues
  remaining: UnitValues
}

export type SibCapacityOverview = {
  year: number
  /** Whether the backend lists a capacity entry for the year at all. */
  quotaKnown: boolean
  services: ServiceCapacity[]
  orders: Order[]
  recipients: RecipientRemaining[]
  /** Performed this calendar month; null when the selected year is not the running one. */
  currentMonth: { month: number; performed: UnitValues } | null
  /** The year's activities of the SIB (all pages), for the progress chart. Empty for a future year. */
  activities: ProgressActivity[]
  /** Hours per unit from the service catalogue, for converting planned hours. */
  hoursPerUnit: Record<string, number>
}

export type SibCapacityState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; data: SibCapacityOverview }

function isServiceCode(code: string): code is SibServiceCode {
  return (SERVICE_CODES as readonly string[]).includes(code)
}

/**
 * Hours from the workload endpoint into the service's unit. This is a unit conversion only - the
 * figures themselves (remaining, performed, pro-rating) come from the backend.
 */
function toUnits(
  buckets: WorkloadBucketDto[],
  field: keyof Omit<WorkloadBucketDto, 'serviceType'>,
  hoursPerUnit: Record<string, number>
): UnitValues {
  const out: UnitValues = {}
  for (const b of buckets) {
    const code = b.serviceType === 'TOJ_CUSTOMER' ? 'TOJ' : b.serviceType
    const hours = b[field]
    if (!isServiceCode(code) || hours == null) continue
    const hpu = getHoursPerUnit(code, hoursPerUnit[code])
    out[code] = Math.round((hours / hpu) * 100) / 100
  }
  return out
}

function yearWindow(year: number) {
  return { from: `${year}-01-01T00:00:00`, to: `${year}-12-31T23:59:59` }
}

function monthWindow(year: number, month: number) {
  const last = new Date(year, month, 0).getDate()
  const mm = String(month).padStart(2, '0')
  return { from: `${year}-${mm}-01T00:00:00`, to: `${year}-${mm}-${last}T23:59:59` }
}

async function loadOverview(
  userId: string,
  year: number,
  capacityYears: CapacityYear[],
  today: Date
): Promise<SibCapacityOverview> {
  const capacityYear = capacityYears.find(y => y.year === year)
  const hoursPerUnit: Record<string, number> = {}
  for (const s of capacityYear?.services ?? []) {
    if (s.service.hoursPerUnit) hoursPerUnit[s.service.code] = s.service.hoursPerUnit
  }

  const isRunningYear = year === today.getFullYear()
  const [yearWorkload, orders, monthWorkload, activities] = await Promise.all([
    getSibWorkload({ userId, ...yearWindow(year) }),
    loadCountedOrders(year),
    isRunningYear
      ? getSibWorkload({ userId, ...monthWindow(year, today.getMonth() + 1) })
      : Promise.resolve(null),
    // GET /activities is scoped to the logged-in SIB by the backend.
    year <= today.getFullYear()
      ? getActivitiesInRange(new Date(year, 0, 1), new Date(year, 11, 31, 23, 59, 59))
      : Promise.resolve({ items: [] }),
  ])

  // Recipients by their stable id, as carried on the orders; legacy orders without one are left
  // out of the per-recipient view rather than merged under a name.
  const customerIds = [...new Set(orders.map(o => Number(o.customerId ?? 0)).filter(id => id > 0))]
  const perRecipient = await Promise.all(
    customerIds.map(async customerId => {
      const res = await getSibWorkload({ userId, customerId, ...yearWindow(year) })
      return {
        customerId,
        ordered: toUnits(res.buckets, 'approvedOrderHours', hoursPerUnit),
        performed: toUnits(res.buckets, 'completedActivityHours', hoursPerUnit),
        remaining: toUnits(res.buckets, 'remainingApprovedOrderHours', hoursPerUnit),
      }
    })
  )

  const free = toUnits(yearWorkload.buckets, 'remainingCapacityHours', hoursPerUnit)
  const services: ServiceCapacity[] = SERVICE_CODES.map(code => {
    const quota = capacityYear?.services.find(s => s.service.code === code)?.capacity ?? null
    return { code, quota, free: quota == null ? null : (free[code] ?? null) }
  })

  return {
    year,
    quotaKnown: capacityYear != null,
    services,
    orders: [...orders].sort((a, b) => a.endTime.localeCompare(b.endTime)),
    recipients: perRecipient,
    currentMonth: monthWorkload
      ? {
          month: today.getMonth() + 1,
          performed: toUnits(monthWorkload.buckets, 'completedActivityHours', hoursPerUnit),
        }
      : null,
    activities: (activities.items ?? []).map(a => ({
      date: String(a.date),
      type: String(a.type),
      timeQuantity: a.timeQuantity == null ? null : Number(a.timeQuantity),
      customerId: a.customerId == null ? null : Number(a.customerId),
    })),
    hoursPerUnit,
  }
}

/**
 * Everything the SIB capacity widget shows for one year. The years the SIB can page through come
 * from GET /settings/orders/years; GET /orders only accepts the previous, current and next year.
 */
export function useSibCapacityOverview(userId: string | undefined, today: Date = new Date()) {
  const [capacityYears, setCapacityYears] = useState<CapacityYear[] | null>(null)
  const [yearsFailed, setYearsFailed] = useState(false)
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [year, setYear] = useState(today.getFullYear())
  const [state, setState] = useState<SibCapacityState>({ status: 'loading' })
  const latest = useRef(0)
  const todayKey = today.toDateString()

  const loadYears = useCallback(async () => {
    setYearsFailed(false)
    try {
      const res = await getOrderCapacityYears()
      setCurrentYear(res.currentYear ?? today.getFullYear())
      setCapacityYears(res.years ?? [])
    } catch {
      setYearsFailed(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayKey])

  useEffect(() => {
    if (userId) loadYears()
  }, [userId, loadYears])

  // Only the latest year's answer may land: paging quickly must not show an older year's figures
  // under the newly selected label.
  const load = useCallback(async () => {
    if (!userId || !capacityYears) return
    const request = ++latest.current
    setState({ status: 'loading' })
    try {
      const data = await loadOverview(userId, year, capacityYears, today)
      if (request === latest.current) setState({ status: 'ready', data })
    } catch {
      if (request === latest.current) setState({ status: 'error' })
    }
    // `today` is compared by day so a new Date each render does not reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, year, capacityYears, todayKey])

  useEffect(() => {
    load()
  }, [load])

  const nextYearOpen = capacityYears?.some(y => y.year === currentYear + 1 && y.open) ?? false
  return {
    state: (yearsFailed ? { status: 'error' } : state) as SibCapacityState,
    year,
    setYear,
    minYear: currentYear - 1,
    maxYear: nextYearOpen ? currentYear + 1 : currentYear,
    retry: yearsFailed ? loadYears : load,
  }
}
